import React, {
	KeyboardEvent,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';
import { TreeDataItem, UpdatingFieldProps } from './TemplateTreeView';
import { Path } from '~/src/pages/bulk-design';
import { Box } from '@mui/material';
import { CheckboxNew, CheckboxO, FolderClose, TooltipHidden, TooltipShow } from './icons';
import { FolderOpen } from '@mui/icons-material';

interface TreeNodeLabelProps {
	node: TreeDataItem;
	path: Path | undefined;
	editMode: boolean;
	showChildren: boolean;
	editModeSelected: boolean | undefined;
	setFieldBasedOnPath: UpdatingFieldProps;
	onShowChildren: () => void;
	onSelectChecbox: () => void;
	onClick: () => void;
}

const TreeNodeLabel = ({
	node,
	path,
	editMode,
	showChildren,
	editModeSelected,
	setFieldBasedOnPath,
	onShowChildren,
	onSelectChecbox,
	onClick,
}: TreeNodeLabelProps) => {
	const [isNameEditing, setIsNameEditing] = useState(false);

	const contentNameEditableRef = useRef<HTMLElement | null>(null);

	const { Name } = node;

	const offset = useMemo(() => {
		return `${
			10 + 22 * ((path || []).length - 1) + ('Item' in node ? 8 : 0)
		}px`;
	}, [path, node]);

	const hasDivider = (path || []).length > 1 && 'Item' in node;

	const handleNameDoubleClick = () => {
		setIsNameEditing(true);
	};

	const handleNameBlur = () => {
		if (path) {
			setFieldBasedOnPath(
				path,
				'Name',
				contentNameEditableRef.current?.innerText || ''
			);
			setIsNameEditing(false);
		}
	};

	const handleEnterPressOnName = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (path) {
				setFieldBasedOnPath(path, 'Name', e.currentTarget.textContent || '');
				setIsNameEditing(false);
			}
		}
	};

	useEffect(() => {
		if (isNameEditing) {
			contentNameEditableRef.current?.focus();
		}
	}, [isNameEditing]);

	return (
		<Box
			className={classNames('tree-label', {
				['tree-label--divider']: hasDivider,
			})}
			style={{ '--tree-label-offset': offset } as React.CSSProperties}
			overflow="hidden"
		>
			{'Items' in node && !showChildren && (
				<TooltipHidden
					onClick={() => !editMode && onShowChildren()}
					className="mx-2"
				/>
			)}
			{'Items' in node && showChildren && (
				<TooltipShow
					onClick={() => !editMode && onShowChildren()}
					className="mx-2 tooltip-show"
				/>
			)}
			<CheckboxO
				onClick={() => onSelectChecbox()}
				className={`${editMode && !editModeSelected ? '' : 'd-none'} me-2`}
				style={{ marginLeft: '2px' }}
			/>
			<CheckboxNew
				onClick={() => onSelectChecbox()}
				className={`${editMode && editModeSelected ? '' : 'd-none'}`}
				style={{ marginRight: '2px' }}
			/>
			{'Items' in node && !showChildren && (
				<FolderClose
					onClick={() => !editMode && onShowChildren()}
					className="me-2"
				/>
			)}
			{'Items' in node && showChildren && (
				<FolderOpen
					onClick={() => !editMode && onShowChildren()}
					className="me-2"
				/>
			)}
			<Box
				component="span"
				textOverflow="ellipsis"
				whiteSpace="nowrap"
				overflow="hidden"
				onDoubleClick={handleNameDoubleClick}
				onClick={onClick}
				onBlur={handleNameBlur}
				onKeyDown={handleEnterPressOnName}
				contentEditable={isNameEditing}
				ref={contentNameEditableRef}
				dangerouslySetInnerHTML={{ __html: Name }}
			/>
		</Box>
	);
};

export default TreeNodeLabel;
